<template>
  <layout :show-profile="true">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 pt-12">
      <div class="flex flex-col">
        <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <!-- Page Header: start -->
          <div class="md:flex md:items-center md:justify-between">
            <div class="flex-1 min-w-0">
              <h2 class="text-2xl font-light leading-7 text-gray-900 sm:text-2xl sm:leading-9 sm:truncate">Partner</h2>
            </div>
            <div class="mt-4 flex md:mt-0 md:ml-4">
              <span class="sm:ml-3 shadow-sm rounded-md">
                <Link :href="route('admin.partners.create')"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-500 bg-gray-300 shadow-sm hover:bg-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400">
                Neuer Partner</Link>
              </span>
            </div>
          </div>
          <!-- Page Header: end -->

          <horizontal-divider />

          <!-- Filter: start -->
          <div class="flex mb-4">
            <div class="w-11/12 mr-2">
              <div class="mt-1 relative rounded-md shadow-sm">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clip-rule="evenodd" />
                  </svg>
                </div>
                <input type="search"
                  class="mt-1 pl-10 focus:ring-gray-400 focus:border-gray-400 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Suchen nach..." v-model="form.search" />
              </div>
            </div>

            <div class="w-1/12 text-center mt-3">
              <button v-if="isFiltered" v-tooltip.left="'Filter zurücksetzen'" @click="reset">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                  stroke="currentColor" class="w-5 h-5 text-gray-500 hover:text-markencode-blue">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>

              <div v-if="!isFiltered">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                  stroke="currentColor" class="w-5 h-5 text-gray-300">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </div>

            </div>
          </div>
          <!-- Filter: end -->

          <!-- List: start -->
          <div class="bg-white shadow overflow-hidden sm:rounded-md">
            <ul>
              <li v-for="(partner, idx) in partners" :key="partner.id" :class="idx ? 'border-t border-gray-200' : ''">
                <a :href="route('admin.partners.edit', { id: partner.id })"
                  class="block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out">
                  <div class="flex items-center px-4 py-4 sm:px-6">
                    <div class="hidden sm:block w-1/5 items-center">
                      <div class="flex-shrink-0">
                        <partner-logo :partner="partner" />
                      </div>
                    </div>

                    <div class="min-w-0 flex-1 px-4 md:grid md:grid-cols-2 md:gap-4">
                      <div>
                        <div class="text-sm leading-5 font-medium text-gray-600 truncate">{{ partner.name }}</div>
                        <div class="text-sm leading-5 font-normal text-gray-400">{{ partner.note }}</div>
                      </div>
                      <div class="hidden md:block">
                        <div>
                          <div class="text-sm leading-5 text-gray-900">
                            <Link :href="route('admin.users.index', { partner_id: partner.id })">{{
    usersInfo(partner.users.length) }}</Link>
                          </div>

                          <div class="mt-2 items-center text-sm">
                            <button v-if="!partner.users.length"
                              class="text-gray-400 focus:outline-none hover:text-markencode-red flex items-center space-x-2"
                              @click.stop.prevent="deletePartner(partner)">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="currentColor" class="w-4 h-4 mr-1 text-gray-500 hover:text-markencode-red">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                              Partner löschen
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>
          <!-- List: end -->
        </div>
      </div>
    </div>

    <confirm-modal v-if="confirmModal.visible" :headline="confirmModal.headline" :text="confirmModal.text"
      @close="confirmModal.visible = false" @confirm="deletePartnerConfirmed"></confirm-modal>
  </layout>
</template>

<script>
import Layout from '@/Pages/ControlPanel/Shared/Layout';
import HorizontalDivider from '@/Shared/HorizontalDivider';

import PartnerLogo from '@/Shared/PartnerLogo.vue';
// TODO: extract the confirmation modal to a decoupled component
import ConfirmModal from '@/Shared/ConfirmModal.vue';
import { pickBy, debounce, mapValues, some, isNotEmpty } from 'lodash';

export default {
  metaInfo: { title: 'Partner' },

  components: {
    Layout,
    HorizontalDivider,

    PartnerLogo,
    ConfirmModal,
  },

  props: ['filters', 'partners'],

  data() {
    return {
      confirmModal: {
        visible: false,
        itemId: null,
        headline: 'Partner löschen',
        text: '',
      },
      form: {
        search: this.filters.search,
      },
    };
  },

  computed: {
    isFiltered() {
      return some(this.form, isNotEmpty);
    },
  },

  watch: {
    form: {
      handler: debounce(function () {
        let query = pickBy(this.form);
        this.$inertia.get(
          this.route(
            'admin.partners.index',
            Object.keys(query).length ? query : { remember: 'forget' }
          )
        );
      }, 250),
      deep: true,
    },
  },

  methods: {
    usersInfo(num) {
      if (!num) {
        return 'Dieser Partner hat noch keine User.';
      }

      if (num == 1) {
        return 'Dieser Partner hat einen User.';
      }

      return `Dieser Partner hat ${num} User.`;
    },
    reset() {
      this.form = mapValues(this.form, () => null);
    },
    deletePartner(partner) {
      this.confirmModal.itemId = partner.id;
      this.confirmModal.text = `Soll der Partner ${partner.name} wirklich gelöscht werden?`;
      this.confirmModal.visible = true;
    },
    deletePartnerConfirmed() {

      if (!this.confirmModal.itemId) return;

      this.sending = true;

      this.$inertia.delete(
        this.route('admin.partners.delete', {
          id: this.confirmModal.itemId,
        }),
        {
          preserveScroll: true,
          onSuccess: (page) => {
            this.sending = false;
            this.confirmModal.visible = false;
          },
        }
      );

    },
  },
};
</script>
